import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "../AuthProvider";
import http from "../api/apiClient";
import { useSupportViewAs } from "../supportViewAs/SupportViewAsProvider";

export const PermissionsCtx = createContext({ ready: false, grants: {} });

export function normalizeGrants(raw) {
  const hasEffectiveGrants = Array.isArray(raw?.effective);
  const entries = Array.isArray(raw) ? raw : hasEffectiveGrants ? raw.effective : raw?.entries;
  if (!Array.isArray(entries)) return {};

  // out: { [resource]: { [action]: true } }
  const out = {};
  if (hasEffectiveGrants) out.__effective = true;

  const ensure = (res) => (out[res] ||= {});
  // out.__scopes: { [resource]: { [action]: ["ANY" | "OWN" | "RELATED"] } }, so checks can tell
  // school-wide or own-work access apart from a parent's access to their own family.
  const scopes = {};

  const set = (res, act, scope) => {
    ensure(res)[act] = true;
    const byAction = (scopes[res] ||= {});
    const list = (byAction[act] ||= []);
    if (!list.includes(scope)) list.push(scope);
  };

  for (const { resource, action, scope } of entries) {
    if (!resource || !action) continue;
    // Missing scope counts as ANY, like the backend does for legacy rows.
    const normalizedScope = typeof scope === "string" && scope.trim() ? scope.trim().toUpperCase() : "ANY";

    // exact
    set(resource, action, normalizedScope);

    // wildcard resource or action support
    if (resource === "*") {
      // exact action on ANY resource
      set("*", action, normalizedScope);
      if (action === "*") {
        // full god-mode
        set("*", "*", normalizedScope);
      }
    }
    if (action === "*") {
      // ANY action on this resource
      set(resource, "*", normalizedScope);
    }
  }
  out.__scopes = scopes;

  const debugFlag = process.env.REACT_APP_API_DEBUG;
  const normalizedFlag =
    typeof debugFlag === "string" ? debugFlag.trim().toLowerCase() : null;
  const debugEnabled = normalizedFlag
    ? ["1", "true", "yes", "on"].includes(normalizedFlag)
    : process.env.NODE_ENV !== "production";
  if (debugEnabled) {
    // eslint-disable-next-line no-console
    console.debug("[authz] grants (normalized)", out);
  }
  return out;
}

export default function PermissionsProvider({ children }) {
  const { authenticated, keycloak } = useContext(AuthContext);
  const supportViewAs = useSupportViewAs();
  const [state, setState] = useState({ ready: false, grants: {} });
  const subject = keycloak?.tokenParsed?.sub || keycloak?.idTokenParsed?.sub || null;
  const supportSubject =
    supportViewAs?.active && supportViewAs?.target
      ? supportViewAs.target.userId || supportViewAs.target.email || null
      : null;
  const supportRoutePath = supportViewAs?.routePath || "";

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!authenticated) {
        setState({ ready: false, grants: {} });
        return;
      }
      // During token refresh Keycloak can briefly have no token; don't blank the UI/grants for that.
      if (!keycloak?.token) return;
      try {
        const { data } = await http.get("/authz/me");
        if (!cancelled) setState({ ready: true, grants: normalizeGrants(data) });
      } catch (e) {
        if (!cancelled) setState({ ready: true, grants: {} });
      }
    };

    load();
    return () => { cancelled = true; };
  // Intentionally *not* dependent on `keycloak.token`: token refreshes frequently and would
  // cause periodic refetch/re-render. We refresh grants when auth state or subject changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, subject, supportSubject, supportRoutePath]);

  return (
    <PermissionsCtx.Provider value={state}>
      {children}
    </PermissionsCtx.Provider>
  );
}

export function usePermissionsCtx() {
  return useContext(PermissionsCtx);
}
